import { Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import SectionCard, { SectionCardList, SectionCardEmpty } from '../../../components/ui/SectionCard';

/**
 * "Repairs Required" dashboard section — sites with open defects.
 *
 * Presentational: the caller owns fetching, so this stays trivially testable.
 * Hides the row layout, the danger edge marker, the empty state, and the
 * site-page link target.
 *
 * Lists every site passed in. On wide layouts the card takes the height of its
 * grid row (set by the neighbouring Active Sessions card) and the list scrolls
 * inside it; on phones the list is height-capped and scrolls the same way.
 *
 * Tapping anywhere on a row opens that site. That is done by stretching the
 * site-name link across the row rather than putting an onClick on the row, so
 * the markup stays valid (no control nested inside a link), the Repair button
 * remains a separate control, and keyboard users get two ordinary tab stops.
 *
 * Props:
 *   sites     — Array<{ id, name, location, repairCount }>
 *   onRepair  — (site) => void
 *   number    — eyebrow passed through to SectionCard
 *   isLoading — shows a loading line instead of the list
 */
export default function RepairsRequiredCard({
  sites = [],
  onRepair,
  number,
  isLoading = false,
}) {
  return (
    <SectionCard
      number={number}
      title="Repairs Required"
      aria-label="Repairs required"
      className="section-card--fit-row"
    >
      {isLoading ? (
        <SectionCardEmpty>Loading repairs…</SectionCardEmpty>
      ) : sites.length === 0 ? (
        <SectionCardEmpty>No repairs outstanding.</SectionCardEmpty>
      ) : (
        <SectionCardList
          className="section-card-list--scroll"
          tabIndex={0}
          aria-label="Sites with repairs required"
        >
          {sites.map((site) => (
            <li key={site.id} className="section-card-row section-card-row--danger">
              <div className="section-card-row-main">
                <Link to={`/sites/${site.id}`} className="section-card-row-link">
                  {/* Inner span carries the ellipsis: overflow on the link itself
                      would clip the ::after that stretches the tap target. */}
                  <span className="section-card-row-name">{site.name}</span>
                </Link>
                <span className="section-card-row-meta">
                  <span className="section-card-row-count">
                    {site.repairCount} repair{site.repairCount === 1 ? '' : 's'}
                  </span>
                  {site.location ? ` · ${site.location}` : null}
                </span>
              </div>
              <Button
                type="button"
                variant="outline"
                className="section-card-row-action"
                onClick={() => onRepair?.(site)}
                aria-label={`Start a repair visit at ${site.name}`}
              >
                Repair
              </Button>
            </li>
          ))}
        </SectionCardList>
      )}
    </SectionCard>
  );
}
